import {
  BaseSystem,
  DroneSystem,
  ExternalSystem,
  InternalSystem,
  SoftwareSystem,
  FuelSystem,
  CargoSystem,
  HangarSystem,
  ArmorySystem,
  EscapePodSystem,
  FuelProcessorSystem,
  FuelScoopSystem,
  FuelTankSystem,
  JumpDriveSystem,
  ManeuverDriveSystem,
  PowerPlantSystem,
  SensorSystem,
  ShieldSystem,
  TurretSystem,
  WeaponSystem,
  StructureSystem,
  OptionalSystem,
  AccommodationSystem,
  BridgeSystem,
} from "../../types/optionalSystems";
import { createSelector } from "@reduxjs/toolkit";
import {
  isAccelerationSystem,
  isBarracksSystem,
  isBrigSystem,
  isCabinSpaceSystem,
  isStateroomSystem,
  isMultiEnvironmentSystem,
} from "../../types/optionalSystems";
import { getDriveTons, getDriveCost } from "../../constants/drives";
import { Drive } from "../../types/drive";

// Type guards
export const isDroneSystem = (system: BaseSystem): system is DroneSystem => {
  return system.type === "drone";
};

export const isFuelSystem = (system: BaseSystem): system is FuelSystem => {
  return system.type === "fuel";
};

export const isCargoSystem = (system: BaseSystem): system is CargoSystem => {
  return system.type === "cargo";
};

export const isHangarSystem = (system: BaseSystem): system is HangarSystem => {
  return system.type === "hangar";
};

export const isArmorySystem = (system: BaseSystem): system is ArmorySystem => {
  return system.type === "armory";
};

export const isEscapePodSystem = (
  system: BaseSystem,
): system is EscapePodSystem => {
  return system.type === "escapePod";
};

export const isFuelProcessorSystem = (
  system: BaseSystem,
): system is FuelProcessorSystem => {
  return system.type === "fuelProcessor";
};

export const isFuelScoopSystem = (
  system: BaseSystem,
): system is FuelScoopSystem => {
  return system.type === "fuelScoop";
};

export const isFuelTankSystem = (
  system: BaseSystem,
): system is FuelTankSystem => {
  return system.type === "fuelTank";
};

export const isJumpDriveSystem = (
  system: BaseSystem,
): system is JumpDriveSystem => {
  return system.type === "jumpDrive";
};

export const isManeuverDriveSystem = (
  system: BaseSystem,
): system is ManeuverDriveSystem => {
  return system.type === "maneuverDrive";
};

export const isPowerPlantSystem = (
  system: BaseSystem,
): system is PowerPlantSystem => {
  return system.type === "powerPlant";
};

export const isSensorSystem = (system: BaseSystem): system is SensorSystem => {
  return system.type === "sensor";
};

export const isShieldSystem = (system: BaseSystem): system is ShieldSystem => {
  return system.type === "shield";
};

export const isTurretSystem = (system: BaseSystem): system is TurretSystem => {
  return system.type === "turret";
};

export const isWeaponSystem = (system: BaseSystem): system is WeaponSystem => {
  return system.type === "weapon";
};

export const isInternalSystem = (
  system: BaseSystem,
): system is InternalSystem => {
  return system.type === "internal";
};

// Calculation interfaces
interface SystemSpecs {
  cost: number;
  tonnage: number;
  power: number;
  bandwidth: number;
  techLevel: number;
  effect: string;
}

interface SoftwareBaseValues {
  cost: number;
  bandwidth: number;
  techLevel: number;
  effect: string;
  hasLevels?: boolean;
  maxLevel?: number;
  hasRange?: boolean;
  hasCrewReplacement?: boolean;
}

// Memoized base values
const softwareBaseValues: Record<string, SoftwareBaseValues> = {
  manoeuvre: {
    cost: 0,
    bandwidth: 0,
    techLevel: 8,
    effect: "Allows basic control of ship.",
  },
  intellect: {
    cost: 0,
    bandwidth: 0,
    techLevel: 11,
    effect: "Allows a ship to understand and obey verbal commands.",
  },
  jumpControl: {
    cost: 0.1,
    bandwidth: 5,
    techLevel: 9,
    effect:
      "Allows jumps of up to the specified number. Incorporates astrogation software and jump engine management.",
    hasLevels: true,
    maxLevel: 6,
  },
  // Add other software base values...
};

// Memoized calculation functions
export const calculateDroneSystemSpecs = (
  type: string,
  size: number,
): SystemSpecs => {
  const baseCost = type === "droneBay" ? 0.5 : 0.1;
  const baseTonnage = type === "droneBay" ? 10 : 2;

  return {
    cost: baseCost * size,
    tonnage: baseTonnage * size,
    power: 0,
    bandwidth: 0,
    techLevel: 0,
    effect: "",
  };
};

export const calculateExternalSystemSpecs = (
  system: ExternalSystem,
  shipTonnage: number,
): SystemSpecs => {
  const specs: SystemSpecs = {
    cost: 0,
    tonnage: 0,
    power: 0,
    bandwidth: 0,
    techLevel: system.techLevel,
    effect: "",
  };

  switch (system.subtype) {
    case "aerofins":
      specs.tonnage = Math.ceil(shipTonnage * 0.05); // 5% of ship tonnage
      specs.cost = specs.tonnage * 100000; // MCr0.1 per ton
      specs.effect = `DM+${system.properties.pilotDM || 2} to Pilot checks in atmosphere`;
      break;

    case "breachingTube":
      specs.cost = 3000000; // MCr3
      specs.tonnage = 3;
      specs.effect = `DM+${system.properties.boardingDM || 1} to Boarding Actions`;
      break;

    case "dockingClamp":
      const clampType = system.properties.clampType || "typeI";
      const clampData = getDockingClampData(clampType);
      specs.cost = clampData.cost;
      specs.tonnage = clampData.tonnage;
      specs.effect = `Can dock ships up to ${clampData.maxTonnage} tons`;
      break;

    case "forcedLinkage":
      const linkageType = system.properties.linkageType || "basic";
      const linkageData = getForcedLinkageData(linkageType);
      specs.cost = linkageData.cost;
      specs.tonnage = 2;
      specs.effect = `Pilot DM ${linkageData.pilotDM >= 0 ? "+" : ""}${linkageData.pilotDM}`;
      break;

    case "grapplingArm":
      const armType = system.properties.armType || "standard";
      const armData = getGrapplingArmData(armType);
      specs.cost = armData.cost;
      specs.tonnage = armData.tonnage;
      specs.effect = `Can manipulate objects up to ${armData.maxWeight} tons`;
      break;

    case "holographicHull":
      specs.cost = shipTonnage * 100000; // Cr100,000 per ton of hull
      specs.tonnage = 0; // No tonnage consumed
      specs.power = Math.ceil(shipTonnage / 2); // 1 power per 2 tons of hull
      specs.effect = "Can change hull appearance";
      break;

    case "towCable":
      specs.tonnage = Math.ceil(shipTonnage * 0.01); // 1% of ship tonnage
      specs.cost = specs.tonnage * 5000; // Cr5,000 per ton
      specs.effect = "Can tow other vessels (affects Thrust, prevents Jump)";
      break;
  }

  return specs;
};

// Helper functions for external system calculations
const getDockingClampData = (clampType: string) => {
  switch (clampType) {
    case "typeI":
      return { cost: 500000, tonnage: 1, maxTonnage: 30 };
    case "typeII":
      return { cost: 1000000, tonnage: 5, maxTonnage: 99 };
    case "typeIII":
      return { cost: 2000000, tonnage: 10, maxTonnage: 300 };
    case "typeIV":
      return { cost: 4000000, tonnage: 20, maxTonnage: 2000 };
    case "typeV":
      return { cost: 8000000, tonnage: 50, maxTonnage: 999999 };
    default:
      return { cost: 500000, tonnage: 1, maxTonnage: 30 };
  }
};

const getForcedLinkageData = (linkageType: string) => {
  switch (linkageType) {
    case "basic":
      return { cost: 50000, pilotDM: -2 };
    case "improved":
      return { cost: 75000, pilotDM: -1 };
    case "enhanced":
      return { cost: 100000, pilotDM: 0 };
    case "advanced":
      return { cost: 500000, pilotDM: 2 };
    default:
      return { cost: 50000, pilotDM: -2 };
  }
};

const getGrapplingArmData = (armType: string) => {
  switch (armType) {
    case "standard":
      return { cost: 1000000, tonnage: 2, maxWeight: 2 };
    case "heavy":
      return { cost: 3000000, tonnage: 6, maxWeight: 10 };
    default:
      return { cost: 1000000, tonnage: 2, maxWeight: 2 };
  }
};

interface InternalSystemProperties {
  tonnage?: number;
  size?: number;
  costPerTon?: number;
  powerPerTon?: number;
  power?: number;
  boobyTrapType?: string;
  types?: Array<{ label: string; cost: number; damage: string }>;
  sensorDM?: number;
  investigateDM?: number;
  tonnageMultiplier?: number;
  tonsPerScientist?: number;
  costPer4Tons?: number;
  lifeSupportPerTon?: number;
  tonsPerUser?: number;
  transferPerTon?: number;
  maxTonnage?: number;
  minTonnage?: number;
  armourPerTon?: number;
  maxArmour?: number;
  hitsPer5Tons?: number;
}

export function calculateInternalSystemSpecs(
  type: string,
  properties: InternalSystemProperties = {},
): SystemSpecs {
  const specs: SystemSpecs = {
    cost: 0,
    tonnage: 0,
    power: 0,
    bandwidth: 0,
    techLevel: 0,
    effect: "",
  };

  // Helper function to safely get numeric properties
  const getNumber = (prop: unknown, defaultValue: number): number => {
    return typeof prop === "number" ? prop : defaultValue;
  };

  switch (type) {
    case "internalAirlock":
      specs.tonnage = Math.max(
        getNumber(properties.tonnage, getNumber(properties.size, 2)),
        2,
      );
      specs.cost = specs.tonnage * getNumber(properties.costPerTon, 100000);
      break;
    case "internalArmoury":
      specs.tonnage = getNumber(properties.tonnage, 1);
      specs.cost = specs.tonnage * getNumber(properties.costPerTon, 250000);
      break;
    case "internalBiosphere":
      specs.tonnage = getNumber(properties.tonnage, 1);
      specs.cost = specs.tonnage * getNumber(properties.costPerTon, 200000);
      specs.power = specs.tonnage * getNumber(properties.powerPerTon, 1);
      specs.effect = `Eliminates life support for ${2 * specs.tonnage} passengers`;
      break;
    case "internalBoobyTrap":
      // Use selected type for cost/damage
      const trapType = (
        typeof properties.boobyTrapType === "string"
          ? properties.boobyTrapType
          : "basic"
      ).toLowerCase();
      const types = Array.isArray(properties.types) ? properties.types : [];
      const trap =
        types.find((t) => t.label.toLowerCase() === trapType) || types[0];
      specs.cost = getNumber(trap?.cost, 100000);
      specs.tonnage = 0;
      specs.effect = `Deals ${trap?.damage || "3D"} per round`;
      break;
    case "internalBriefingRoom":
      specs.tonnage = 4;
      specs.cost = 500000;
      break;
    case "internalConcealedCompartment":
      specs.tonnage = getNumber(properties.tonnage, 1);
      specs.cost = specs.tonnage * getNumber(properties.costPerTon, 20000);
      specs.effect = `DM${getNumber(properties.sensorDM, -2)} to Sensors, DM${getNumber(properties.investigateDM, -4)} to Investigate`;
      break;
    case "internalConstructionDeck":
      specs.tonnage = getNumber(properties.tonnage, 1);
      specs.cost = specs.tonnage * getNumber(properties.costPerTon, 500000);
      specs.power = specs.tonnage * getNumber(properties.powerPerTon, 1);
      specs.effect = `Can build/repair ships up to ${0.5 * specs.tonnage} tons`;
      break;
    case "internalDockingSpace":
      specs.tonnage = getNumber(properties.tonnage, 1);
      specs.cost = specs.tonnage * getNumber(properties.costPerTon, 250000);
      specs.effect = `+10% tonnage for largest ship`;
      break;
    case "internalFullHangar":
      specs.tonnage =
        getNumber(properties.tonnage, 1) *
        getNumber(properties.tonnageMultiplier, 2);
      specs.cost = specs.tonnage * getNumber(properties.costPerTon, 200000);
      break;
    case "internalGravScreen":
      specs.tonnage = getNumber(properties.tonnage, 1);
      specs.cost = specs.tonnage * getNumber(properties.costPerTon, 1000000);
      specs.power = specs.tonnage * getNumber(properties.powerPerTon, 2);
      break;
    case "internalLaboratory":
      specs.tonnage = getNumber(properties.tonnage, 4);
      specs.cost =
        Math.ceil(specs.tonnage / getNumber(properties.tonsPerScientist, 4)) *
        getNumber(properties.costPer4Tons, 1000000);
      break;
    case "internalLaunchTube":
      specs.tonnage =
        getNumber(properties.tonnage, 1) *
        getNumber(properties.tonnageMultiplier, 10);
      specs.cost = specs.tonnage * getNumber(properties.costPerTon, 500000);
      specs.power = specs.tonnage * getNumber(properties.powerPerTon, 1);
      break;
    case "internalLibrary":
      specs.tonnage = 4;
      specs.cost = 100000;
      break;
    case "internalMedicalBay":
      specs.tonnage = 4;
      specs.cost = 500000;
      specs.power = 2;
      break;
    case "internalReEntryCapsule":
      specs.tonnage = 1;
      specs.cost = 100000;
      break;
    case "internalReEntryPod":
      specs.tonnage = 2;
      specs.cost = 200000;
      break;
    case "internalRecoveryDeck":
      specs.tonnage =
        getNumber(properties.tonnage, 1) *
        getNumber(properties.tonnageMultiplier, 10);
      specs.cost = specs.tonnage * getNumber(properties.costPerTon, 500000);
      specs.power = specs.tonnage * getNumber(properties.powerPerTon, 1);
      break;
    case "internalStable":
      specs.tonnage = Math.max(getNumber(properties.tonnage, 10), 10);
      specs.cost = specs.tonnage * getNumber(properties.costPerTon, 2500);
      specs.effect = `Life support: Cr${getNumber(properties.lifeSupportPerTon, 250) * specs.tonnage}`;
      break;
    case "internalStudio":
      specs.tonnage = getNumber(properties.tonnage, 4);
      specs.cost = specs.tonnage * getNumber(properties.costPerTon, 100000);
      specs.effect = `1 user per ${getNumber(properties.tonsPerUser, 4)} tons`;
      break;
    case "internalTrainingFacilities":
      specs.tonnage = getNumber(properties.tonnage, 2);
      specs.cost = specs.tonnage * getNumber(properties.costPerTon, 200000);
      specs.effect = `2 tons per trainee`;
      break;
    case "internalUnrepSystem":
      specs.tonnage = getNumber(properties.tonnage, 1);
      specs.cost = specs.tonnage * getNumber(properties.costPerTon, 500000);
      specs.power = specs.tonnage * getNumber(properties.powerPerTon, 1);
      specs.effect = `Transfers ${getNumber(properties.transferPerTon, 20)} tons/hour per ton`;
      break;
    case "internalVault":
      specs.tonnage = Math.max(
        Math.min(
          getNumber(properties.tonnage, 4),
          getNumber(properties.maxTonnage, 40),
        ),
        getNumber(properties.minTonnage, 4),
      );
      specs.cost = specs.tonnage * getNumber(properties.costPerTon, 500000);
      specs.effect = `Armour: ${Math.min(specs.tonnage * getNumber(properties.armourPerTon, 1), getNumber(properties.maxArmour, 10))}, Hits: ${Math.floor(specs.tonnage / 5) * getNumber(properties.hitsPer5Tons, 1)}`;
      break;
    case "internalWorkshop":
      specs.tonnage = 6;
      specs.cost = 300000;
      specs.power = 1;
      break;
    default:
      // fallback for unknown types
      specs.tonnage = getNumber(properties.tonnage, 1);
      specs.cost = specs.tonnage * getNumber(properties.costPerTon, 0);
      specs.power = getNumber(properties.power, 0);
      break;
  }

  return specs;
}

export const calculateSoftwareSystemSpecs = (
  type: string,
  level: number = 1,
): SystemSpecs => {
  const base = softwareBaseValues[type] || {
    cost: 0,
    bandwidth: 0,
    techLevel: 8,
    effect: "",
  };
  const specs: SystemSpecs = {
    cost: base.cost,
    tonnage: 0,
    power: 0,
    bandwidth: base.bandwidth,
    techLevel: base.techLevel,
    effect: base.effect,
  };

  if (base.hasLevels) {
    switch (type) {
      case "jumpControl":
        specs.cost = base.cost * level;
        break;
      case "evade":
        specs.cost = base.cost * level;
        specs.bandwidth = base.bandwidth + (level - 1) * 5;
        break;
      // Add other software calculations
    }
  }

  return specs;
};

// Memoized validation functions
export const validateDroneSystem = (system: DroneSystem): string[] => {
  const errors: string[] = [];

  if (!system.subtype) {
    errors.push("Drone type is required");
  }

  const size = system.properties?.size;
  if (typeof size === "number" && size < 2) {
    errors.push("Drone size must be at least 2 tons");
  }

  return errors;
};

export const validateExternalSystem = (
  system: ExternalSystem,
  shipTonnage: number,
  techLevel: number,
  tlOverrideActive: boolean = false,
): string[] => {
  const errors: string[] = [];

  if (!system.subtype) {
    errors.push("External system type is required");
    return errors;
  }

  // Check tech level requirements
  if (!tlOverrideActive && techLevel < system.techLevel) {
    errors.push(
      `${system.name} requires Tech Level ${system.techLevel} or higher`,
    );
  }

  switch (system.subtype) {
    case "aerofins":
      if (shipTonnage < 1) {
        errors.push("Aerofins require valid ship tonnage");
      }
      break;

    case "breachingTube":
      // No additional validation needed - fixed cost and tonnage
      break;

    case "dockingClamp":
      const clampType = system.properties.clampType || "typeI";
      const validTypes = ["typeI", "typeII", "typeIII", "typeIV", "typeV"];
      if (!validTypes.includes(clampType)) {
        errors.push("Invalid docking clamp type");
      }
      break;

    case "forcedLinkage":
      const linkageType = system.properties.linkageType || "basic";
      const validLinkageTypes = ["basic", "improved", "enhanced", "advanced"];
      if (!validLinkageTypes.includes(linkageType)) {
        errors.push("Invalid forced linkage type");
      }
      break;

    case "grapplingArm":
      const armType = system.properties.armType || "standard";
      const validArmTypes = ["standard", "heavy"];
      if (!validArmTypes.includes(armType)) {
        errors.push("Invalid grappling arm type");
      }
      break;

    case "holographicHull":
      if (shipTonnage < 1) {
        errors.push("Holographic hull requires valid ship tonnage");
      }
      break;

    case "towCable":
      if (shipTonnage < 1) {
        errors.push("Tow cable requires valid ship tonnage");
      }
      break;
  }

  return errors;
};

export function validateInternalSystem(
  system: InternalSystem,
  shipTonnage: number,
  weightOverrideActive: boolean = false,
): string[] {
  const errors: string[] = [];

  // Helper function to safely get numeric properties
  const getNumber = (prop: unknown, defaultValue: number): number => {
    return typeof prop === "number" ? prop : defaultValue;
  };

  // Common checks
  if (!weightOverrideActive && getNumber(system.properties.size, 0) <= 0) {
    errors.push("Size must be positive");
  }

  // Type-specific checks
  switch (system.subtype) {
    case "internalAirlock":
      if (!weightOverrideActive && getNumber(system.properties.size, 0) < 2) {
        errors.push("Airlock size must be at least 2 tons");
      }
      break;
    case "internalArmoury":
      if (
        system.properties.crewCapacity != null &&
        system.properties.crewCapacity < 1
      ) {
        errors.push("Armoury must have a capacity of at least 1");
      }
      break;
    case "internalBiosphere":
      if (!weightOverrideActive && system.properties.tonnage != null && system.properties.tonnage < 1) {
        errors.push("Biosphere must be at least 1 ton");
      }
      break;
    case "internalBoobyTrap":
      if (!system.properties.boobyTrapType) {
        errors.push("Booby trap type is required");
      }
      break;
    case "internalConcealedCompartment":
      if (!weightOverrideActive && getNumber(system.properties.tonnage, 1) < 1) {
        errors.push("Concealed compartment must be at least 1 ton");
      }
      if (!weightOverrideActive &&
        getNumber(system.properties.tonnage, 1) >
        (getNumber(system.properties.maxPercentOfShip, 5) / 100) *
          getNumber(system.properties.shipTonnage, 1000)
      ) {
        errors.push("Concealed compartment exceeds 5% of ship tonnage");
      }
      break;
    case "internalConstructionDeck":
      if (system.properties.tonnage != null && system.properties.tonnage < 1) {
        errors.push("Construction deck must be at least 1 ton");
      }
      break;
    case "internalDockingSpace":
      if (system.properties.tonnage != null && system.properties.tonnage < 1) {
        errors.push("Docking space must be at least 1 ton");
      }
      break;
    case "internalFullHangar":
      if (system.properties.tonnage != null && system.properties.tonnage < 1) {
        errors.push("Full hangar must be at least 1 ton");
      }
      break;
    case "internalGravScreen":
      if (system.properties.tonnage != null && system.properties.tonnage < 1) {
        errors.push("Grav screen must be at least 1 ton");
      }
      break;
    case "internalLaboratory":
      if (system.properties.tonnage != null && system.properties.tonnage < 4) {
        errors.push("Laboratory must be at least 4 tons");
      }
      break;
    case "internalLaunchTube":
      if (system.properties.tonnage != null && system.properties.tonnage < 1) {
        errors.push("Launch tube must be at least 1 ton");
      }
      break;
    case "internalMedicalBay":
      // No special validation
      break;
    case "internalReEntryCapsule":
      // No special validation
      break;
    case "internalReEntryPod":
      // No special validation
      break;
    case "internalRecoveryDeck":
      if (system.properties.tonnage != null && system.properties.tonnage < 1) {
        errors.push("Recovery deck must be at least 1 ton");
      }
      break;
    case "internalStable":
      if (!weightOverrideActive && system.properties.tonnage != null && system.properties.tonnage < 10) {
        errors.push("Stable must be at least 10 tons");
      }
      break;
    case "internalStudio":
      if (!weightOverrideActive && system.properties.tonnage != null && system.properties.tonnage < 4) {
        errors.push("Studio must be at least 4 tons");
      }
      break;
    case "internalTrainingFacilities":
      if (!weightOverrideActive && system.properties.tonnage != null && system.properties.tonnage < 2) {
        errors.push("Training facilities must be at least 2 tons");
      }
      break;
    case "internalUnrepSystem":
      if (!weightOverrideActive && system.properties.tonnage != null && system.properties.tonnage < 1) {
        errors.push("UNREP system must be at least 1 ton");
      }
      break;
    case "internalVault":
      if (!weightOverrideActive &&
        system.properties.tonnage != null &&
        (system.properties.tonnage < 4 || system.properties.tonnage > 40)
      ) {
        errors.push("Vault must be between 4 and 40 tons");
      }
      break;
    case "internalWorkshop":
      // No special validation
      break;
    default:
      // fallback for unknown types
      break;
  }
  return errors;
}

export const validateSoftwareSystem = (system: SoftwareSystem): string[] => {
  const errors: string[] = [];
  if (!system.properties) {
    errors.push("Software system properties are not defined");
    return errors;
  }

  const bandwidth = system.properties.bandwidth;
  if (typeof bandwidth === "number" && bandwidth < 0) {
    errors.push("Bandwidth cannot be negative");
  }

  const level = system.properties.level;
  if (typeof level === "number" && level < 1) {
    errors.push("Software level must be at least 1");
  }

  return errors;
};

// Memoized selectors for calculations
export const selectTotalSystemCost = createSelector(
  (systems: BaseSystem[]) => systems,
  (systems) => systems.reduce((total, system) => total + system.cost, 0),
);

export const selectTotalSystemTonnage = createSelector(
  (systems: BaseSystem[]) => systems,
  (systems) => systems.reduce((total, system) => total + system.tonnage, 0),
);

export const selectTotalSystemPower = createSelector(
  (systems: BaseSystem[]) => systems,
  (systems) => systems.reduce((total, system) => total + system.power, 0),
);

// Validation functions
export const validateAccommodationSystem = (
  system: AccommodationSystem,
  weightOverrideActive: boolean = false,
): string[] => {
  const errors: string[] = [];

  if (isAccelerationSystem(system)) {
    const seats = system.properties.seats ?? 0;
    if (!weightOverrideActive && seats < 1) {
      errors.push("Acceleration system must have at least 1 seat");
    }
  }

  if (isBarracksSystem(system)) {
    const passengers = system.properties.passengers ?? 0;
    if (!weightOverrideActive && passengers < 1) {
      errors.push("Barracks must accommodate at least 1 passenger");
    }
  }

  if (isBrigSystem(system)) {
    const prisoners = system.properties.prisoners ?? 0;
    if (!weightOverrideActive && prisoners < 1) {
      errors.push("Brig must accommodate at least 1 prisoner");
    }
    if (!weightOverrideActive && prisoners > 12) {
      errors.push("Brig cannot accommodate more than 12 prisoners");
    }
  }

  if (isCabinSpaceSystem(system)) {
    const additionalPassengers = system.properties.additionalPassengers ?? 0;
    if (!weightOverrideActive && additionalPassengers < 1) {
      errors.push(
        "Cabin space must accommodate at least 1 additional passenger",
      );
    }
  }

  if (isStateroomSystem(system)) {
    const size = system.properties.size ?? 0;
    if (!weightOverrideActive && size < 6) {
      errors.push("Stateroom must be at least 6 tons");
    }
  }

  if (isMultiEnvironmentSystem(system)) {
    const environmentTonnage = system.properties.environmentTonnage ?? 0;
    if (!weightOverrideActive && environmentTonnage < 1) {
      errors.push("Multi-environment space must be at least 1 ton");
    }
    if (!system.properties.environmentType) {
      errors.push("Multi-environment space must specify environment type");
    }
  }

  return errors;
};

export const validateOptionalSystem = (
  system: OptionalSystem,
  shipTonnage: number = 0,
  techLevel: number = 8,
  tlOverrideActive: boolean = false,
  weightOverrideActive: boolean = false,
): string[] => {
  const errors: string[] = [];

  switch (system.type) {
    case "drone":
      errors.push(...validateDroneSystem(system));
      break;
    case "external":
      errors.push(...validateExternalSystem(system, shipTonnage, techLevel, tlOverrideActive));
      break;
    case "internal":
      errors.push(...validateInternalSystem(system, shipTonnage, weightOverrideActive));
      break;
    case "software":
      errors.push(...validateSoftwareSystem(system));
      break;
    case "fuel":
      errors.push(...validateFuelSystem(system));
      break;
    case "cargo":
      // TODO: Pass correct shipTonnage, techLevel, isStreamlined, isDispersedStructure from context
      errors.push(...validateCargoSystem(system, 0, 0, false, false, tlOverrideActive));
      break;
    case "hangar":
      errors.push(...validateHangarSystem(system));
      break;
    case "armory":
      errors.push(...validateArmorySystem(system));
      break;
    case "escapePod":
      errors.push(...validateEscapePodSystem(system));
      break;
    case "fuelProcessor":
      errors.push(...validateFuelProcessorSystem(system));
      break;
    case "fuelScoop":
      errors.push(...validateFuelScoopSystem(system));
      break;
    case "fuelTank":
      errors.push(...validateFuelTankSystem(system));
      break;
    case "jumpDrive":
      errors.push(...validateJumpDriveSystem(system));
      break;
    case "maneuverDrive":
      errors.push(...validateManeuverDriveSystem(system));
      break;
    case "powerPlant":
      errors.push(...validatePowerPlantSystem(system));
      break;
    case "sensor":
      errors.push(...validateSensorSystem(system));
      break;
    case "shield":
      errors.push(...validateShieldSystem(system));
      break;
    case "turret":
      errors.push(...validateTurretSystem(system));
      break;
    case "weapon":
      errors.push(...validateWeaponSystem(system));
      break;
    case "structure":
      errors.push(...validateStructureSystem(system));
      break;
    default:
      errors.push(`Unknown system type: ${(system as any).type}`);
  }

  return errors;
};

/**
 * Calculate the total cost of optional systems
 * @param optionalSystems - Array of optional systems
 * @returns Total cost of all optional systems
 */
export const calculateOptionalSystemsCost = (
  optionalSystems: OptionalSystem[],
): number => {
  return optionalSystems.reduce((totalCost, system) => {
    return totalCost + system.cost;
  }, 0);
};

/**
 * Calculate Concealed Maneuver Drive cost and tonnage based on existing maneuver drive
 * @param system - The Concealed Maneuver Drive system
 * @param shipSize - The ship size in tons
 * @param existingDrives - Array of existing drives
 * @returns Object with calculated cost and tonnage
 */
export const calculateConcealedManeuverDriveStats = (
  system: OptionalSystem,
  shipSize: number,
  existingDrives: Drive[] = [],
): { cost: number; tonnage: number; power: number } => {
  // Find the existing maneuver drive
  const maneuverDrive = existingDrives.find(
    (drive) => drive.type === "maneuver",
  );

  if (!maneuverDrive) {
    // No maneuver drive found, return 0 values
    return { cost: 0, tonnage: 0, power: 0 };
  }

  // Calculate base maneuver drive stats
  const baseTons = getDriveTons(
    maneuverDrive.type,
    maneuverDrive.rating,
    shipSize,
  );
  const baseCost = getDriveCost(
    maneuverDrive.type,
    maneuverDrive.rating,
    shipSize,
  );

  // Type guard for multipliers
  let costMultiplier = 0.25;
  let tonnageMultiplier = 0.25;
  const props = system.properties as Record<string, unknown>;
  if (props && typeof props.costMultiplier === "number") {
    costMultiplier = props.costMultiplier;
  }
  if (props && typeof props.tonnageMultiplier === "number") {
    tonnageMultiplier = props.tonnageMultiplier;
  }

  const concealedCost = baseCost * costMultiplier;
  const concealedTonnage = baseTons * tonnageMultiplier;

  // Concealed maneuver drives use the same power as the base drive
  // TODO: Calculate power using a helper if needed
  const concealedPower = 0;

  return {
    cost: concealedCost,
    tonnage: concealedTonnage,
    power: concealedPower,
  };
};

// Validation function stubs for optional system types
export const validateStructureSystem = (system: StructureSystem): string[] => {
  const errors: string[] = [];

  if (!system.id) {
    errors.push("Structure system ID is required");
  }

  if (!system.name) {
    errors.push("Structure system name is required");
  }

  if (system.cost < 0) {
    errors.push("Structure system cost cannot be negative");
  }

  if (system.tonnage < 0) {
    errors.push("Structure system tonnage cannot be negative");
  }

  if (system.power < 0) {
    errors.push("Structure system power cannot be negative");
  }

  if (system.techLevel < 0) {
    errors.push("Structure system tech level cannot be negative");
  }

  // Validate specific properties based on subtype
  switch (system.subtype) {
    case "armouredBulkhead":
      if (!system.properties.protectedArea) {
        errors.push("Armoured bulkhead must specify protected area");
      }
      if (!system.properties.costPerTon || system.properties.costPerTon <= 0) {
        errors.push("Armoured bulkhead must have valid cost per ton");
      }
      break;
    case "adjustableHull":
      if (
        !system.properties.reconfigurationTime ||
        system.properties.reconfigurationTime <= 0
      ) {
        errors.push("Adjustable hull must have valid reconfiguration time");
      }
      if (
        !system.properties.costPer100Tons ||
        system.properties.costPer100Tons <= 0
      ) {
        errors.push("Adjustable hull must have valid cost per 100 tons");
      }
      break;
    case "pressureHull":
      if (
        !system.properties.pressureResistance ||
        system.properties.pressureResistance <= 0
      ) {
        errors.push("Pressure hull must have valid pressure resistance");
      }
      if (
        !system.properties.armourBonus ||
        system.properties.armourBonus <= 0
      ) {
        errors.push("Pressure hull must have valid armour bonus");
      }
      if (!system.properties.costPerTon || system.properties.costPerTon <= 0) {
        errors.push("Pressure hull must have valid cost per ton");
      }
      break;
    default:
      errors.push(`Unknown structure system subtype: ${system.subtype}`);
  }

  return errors;
};

export const validateFuelSystem = (system: FuelSystem): string[] => {
  const errors: string[] = [];
  // TODO: Implement fuel system validation
  // Check for required properties based on subtype
  return errors;
};

// Cargo System Calculations
export const calculateCargoCraneStats = (
  cargoSpace: number,
): { cost: number; tonnage: number; power: number } => {
  const tonnage = 2.5 + Math.ceil(cargoSpace / 150) * 0.5; // 2.5 + 0.5 per 150 tons (or part thereof)
  const cost = tonnage * 1000000; // MCr1 per ton of crane
  return {
    cost,
    tonnage,
    power: 0,
  };
};

export const calculateExternalCargoMountStats = (
  externalCargoCapacity: number,
): { cost: number; tonnage: number; power: number } => {
  const cost = externalCargoCapacity * 1000; // Cr1000 per ton

  return {
    cost,
    tonnage: 0, // No additional tonnage, uses external space
    power: 0,
  };
};

export const calculateJumpNetStats = (
  cargoCapacity: number,
  isInterstellar: boolean = false,
): { cost: number; tonnage: number; power: number } => {
  const costPerTon = isInterstellar ? 300000 : 100000; // MCr0.3 or MCr0.1 per ton
  const cost = cargoCapacity * costPerTon;
  const tonnage = Math.ceil(cargoCapacity / 100); // 1 per 100 tons of cargo

  return {
    cost,
    tonnage,
    power: 0,
  };
};

export const validateCargoSystem = (
  system: CargoSystem,
  shipTonnage: number,
  techLevel: number,
  isStreamlined: boolean,
  isDispersedStructure: boolean,
  tlOverrideActive: boolean = false,
): string[] => {
  const errors: string[] = [];

  switch (system.subtype) {
    case "cargoCrane":
      if (!system.properties.cargoSpace || system.properties.cargoSpace <= 0) {
        errors.push("Cargo space must be specified for cargo crane.");
      }
      if (
        system.properties.cargoSpace &&
        system.properties.cargoSpace > shipTonnage * 0.95
      ) {
        errors.push("Cargo space cannot exceed 95% of ship tonnage.");
      }
      break;

    case "cargoNet":
      // No specific validation needed
      break;

    case "cargoScoop":
      // No specific validation needed
      break;

    case "externalCargoMount":
      if (
        !system.properties.externalCargoCapacity ||
        system.properties.externalCargoCapacity <= 0
      ) {
        errors.push("External cargo capacity must be specified.");
      }
      if (isStreamlined) {
        errors.push(
          "External cargo mounts cannot be used with streamlined hulls.",
        );
      }
      if (isDispersedStructure) {
        errors.push(
          "External cargo mounts cannot be used with dispersed structure hulls.",
        );
      }
      break;

    case "jumpNetInterplanetary":
      if (
        !system.properties.jumpNetCargoCapacity ||
        system.properties.jumpNetCargoCapacity <= 0
      ) {
        errors.push("Jump net cargo capacity must be specified.");
      }
      if (!tlOverrideActive && techLevel < 8) {
        errors.push("Interplanetary jump net requires TL8 or higher.");
      }
      break;

    case "jumpNetInterstellar":
      if (
        !system.properties.jumpNetCargoCapacity ||
        system.properties.jumpNetCargoCapacity <= 0
      ) {
        errors.push("Jump net cargo capacity must be specified.");
      }
      if (!tlOverrideActive && techLevel < 10) {
        errors.push("Interstellar jump net requires TL10 or higher.");
      }
      break;

    case "loadingBelt7":
      if (!tlOverrideActive && techLevel < 7) {
        errors.push("Loading Belt (TL7) requires TL7 or higher.");
      }
      break;

    case "loadingBelt12":
      if (!tlOverrideActive && techLevel < 12) {
        errors.push("Loading Belt (TL12) requires TL12 or higher.");
      }
      break;
  }

  return errors;
};

export const validateSensorSystem = (system: SensorSystem): string[] => {
  const errors: string[] = [];
  // TODO: Implement sensor system validation
  // Check for required properties based on subtype
  return errors;
};

export const validateHangarSystem = (system: HangarSystem): string[] => {
  const errors: string[] = [];
  // TODO: Implement hangar system validation
  // Check for required properties based on subtype
  return errors;
};

export const validateArmorySystem = (system: ArmorySystem): string[] => {
  const errors: string[] = [];
  // TODO: Implement armory system validation
  // Check for required properties based on subtype
  return errors;
};

export const validateEscapePodSystem = (system: EscapePodSystem): string[] => {
  const errors: string[] = [];
  // TODO: Implement escape pod system validation
  // Check for required properties based on subtype
  return errors;
};

export const validateFuelProcessorSystem = (
  system: FuelProcessorSystem,
): string[] => {
  const errors: string[] = [];
  // TODO: Implement fuel processor system validation
  // Check for required properties based on subtype
  return errors;
};

export const validateFuelScoopSystem = (system: FuelScoopSystem): string[] => {
  const errors: string[] = [];
  // TODO: Implement fuel scoop system validation
  // Check for required properties based on subtype
  return errors;
};

export const validateFuelTankSystem = (system: FuelTankSystem): string[] => {
  const errors: string[] = [];
  // TODO: Implement fuel tank system validation
  // Check for required properties based on subtype
  return errors;
};

export const validateJumpDriveSystem = (system: JumpDriveSystem): string[] => {
  const errors: string[] = [];
  // TODO: Implement jump drive system validation
  // Check for required properties based on subtype
  return errors;
};

export const validateManeuverDriveSystem = (
  system: ManeuverDriveSystem,
): string[] => {
  const errors: string[] = [];
  // TODO: Implement maneuver drive system validation
  // Check for required properties based on subtype
  return errors;
};

export const validatePowerPlantSystem = (
  system: PowerPlantSystem,
): string[] => {
  const errors: string[] = [];
  // TODO: Implement power plant system validation
  // Check for required properties based on subtype
  return errors;
};

export const validateShieldSystem = (system: ShieldSystem): string[] => {
  const errors: string[] = [];
  // TODO: Implement shield system validation
  // Check for required properties based on subtype
  return errors;
};

export const validateTurretSystem = (system: TurretSystem): string[] => {
  const errors: string[] = [];
  // TODO: Implement turret system validation
  // Check for required properties based on subtype
  return errors;
};

export const validateWeaponSystem = (system: WeaponSystem): string[] => {
  const errors: string[] = [];
  // TODO: Implement weapon system validation
  // Check for required properties based on subtype
  return errors;
};

// Accommodation system calculations
export function calculateAccelerationBenchSpecs(quantity: number = 1) {
  return {
    cost: 10000 * quantity,
    tonnage: 1 * quantity,
    power: 0,
    lifeSupportCost: 0,
    seats: 4 * quantity,
  };
}

export function calculateAccelerationSeatSpecs(quantity: number = 1) {
  return {
    cost: 30000 * quantity,
    tonnage: 0.5 * quantity,
    power: 0,
    lifeSupportCost: 0,
    seats: 1 * quantity,
  };
}

export function calculateBarracksSpecs(passengers: number = 1) {
  return {
    cost: 50000 * passengers, // 1 passenger per ton
    tonnage: 1 * passengers,
    power: 0,
    lifeSupportCost: 500 * passengers,
    passengers,
  };
}

export function calculateBrigSpecs(quantity: number = 1) {
  return {
    cost: 250000 * quantity,
    tonnage: 4 * quantity,
    power: 0,
    lifeSupportCost: 1000 * quantity,
    prisoners: 6 * quantity,
    maxPrisoners: 12 * quantity,
  };
}

export function calculateCabinSpaceSpecs(passengers: number = 1) {
  return {
    cost: 50000 * 1.5 * passengers, // 1 passenger per 1.5 tons
    tonnage: 1.5 * passengers,
    power: 0,
    lifeSupportCost: 250 * 1.5 * passengers,
    passengers,
  };
}

export function calculateMultiEnvironmentSpaceSpecs(tons: number = 1) {
  const tonnage = tons * 1.05; // +5% for equipment
  return {
    cost: 500000 * tons,
    tonnage,
    power: 1 * tons,
    lifeSupportCost: 0,
  };
}

export function calculateHighStateroomSpecs(quantity: number = 1) {
  return {
    cost: 800000 * quantity,
    tonnage: 6 * quantity,
    power: 0,
    lifeSupportCost: 3000 * quantity,
    highPassengerDM: 1,
  };
}

export function calculateLuxuryStateroomSpecs(quantity: number = 1) {
  return {
    cost: 1500000 * quantity,
    tonnage: 10 * quantity,
    power: 0,
    lifeSupportCost: 5000 * quantity,
    highPassengerDM: 2,
  };
}

// Bridge System Calculations
export const calculateDetachableBridgeStats = (
  system: BridgeSystem,
  shipSize: number,
): {
  cost: number;
  tonnage: number;
  power: number;
  calculationNote: string;
} => {
  if (system.subtype !== "detachable") {
    return { cost: 0, tonnage: 0, power: 0, calculationNote: "" };
  }

  // Get minimum bridge size for this ship size
  const minimumSizes =
    (system.properties.minimumSizes as Record<number, number>) || {};
  let minimumSize = 80; // Default for largest ships
  if (shipSize <= 200) {
    minimumSize = minimumSizes[200] || 15;
  } else if (shipSize <= 1000) {
    minimumSize = minimumSizes[1000] || 30;
  } else if (shipSize <= 2000) {
    minimumSize = minimumSizes[2000] || 50;
  } else {
    minimumSize = minimumSizes[Infinity] || 80;
  }

  // Always use minimum size + 20%
  const finalTonnage = Math.ceil(minimumSize * 1.2);
  const hundredsOfTons = Math.ceil(finalTonnage / 100);
  const cost = hundredsOfTons * 500000 * 1.5;

  const calculationNote = `Detachable Bridge requires a minimum size for this ship (${minimumSize} tons). The detachable version is 20% larger (${finalTonnage} tons total). Cost is calculated as (bridge tons / 100, rounded up) × 500,000 × 1.5.`;

  return {
    cost,
    tonnage: finalTonnage,
    power: system.power || 0,
    calculationNote,
  };
};

export const calculateHolographicControlsStats = (
  system: BridgeSystem,
  baseBridgeCost: number,
): { cost: number; tonnage: number; power: number } => {
  if (system.subtype !== "holographic") {
    return { cost: 0, tonnage: 0, power: 0 };
  }

  // Calculate cost as +25% of base bridge cost
  const costMultiplier = system.properties.costMultiplier || 0.25;
  const cost = Math.floor(baseBridgeCost * costMultiplier);

  return {
    cost,
    tonnage: 0, // No additional tonnage
    power: system.power || 1,
  };
};

export const calculateSensorStationStats = (
  system: BridgeSystem,
  shipSize: number,
): { cost: number; tonnage: number; power: number } => {
  if (system.subtype !== "sensorStation") {
    return { cost: 0, tonnage: 0, power: 0 };
  }

  // Check if ship size allows sensor stations (max 7,500 tons)
  const maxShipTonnage = system.properties.maxShipTonnage || 7500;
  if (shipSize > maxShipTonnage) {
    return { cost: 0, tonnage: 0, power: 0 };
  }

  return {
    cost: system.cost || 500000, // MCr0.5
    tonnage: system.tonnage || 1,
    power: system.power || 0.5,
  };
};

export const validateBridgeSystem = (
  system: BridgeSystem,
  shipSize: number,
  baseBridgeCost: number = 0,
  baseBridgeTonnage: number = 0,
  tlOverrideActive: boolean = false,
  sizeOverrideActive: boolean = false,
): string[] => {
  const errors: string[] = [];

  if (!system.subtype) {
    errors.push("Bridge system subtype is required");
  }

  if (system.subtype === "detachable") {
    // Validate detachable bridge requirements
    const minimumSizes =
      (system.properties.minimumSizes as Record<number, number>) || {};
    let minimumSize = 80;

    if (shipSize <= 200) {
      minimumSize = minimumSizes[200] || 15;
    } else if (shipSize <= 1000) {
      minimumSize = minimumSizes[1000] || 30;
    } else if (shipSize <= 2000) {
      minimumSize = minimumSizes[2000] || 50;
    } else {
      minimumSize = minimumSizes[Infinity] || 80;
    }

    if (baseBridgeTonnage < minimumSize) {
      errors.push(
        `Detachable bridge requires minimum ${minimumSize} tons for a ship of ${shipSize} tons`,
      );
    }
  }

  if (system.subtype === "holographic") {
    // Validate holographic controls requirements
    if (!tlOverrideActive && system.techLevel < 9) {
      errors.push("Holographic controls require Tech Level 9 or higher");
    }
  }

  if (system.subtype === "sensorStation") {
    // Validate sensor station requirements
    const maxShipTonnage = system.properties.maxShipTonnage || 7500;
    if (!sizeOverrideActive && shipSize > maxShipTonnage) {
      errors.push(
        `Sensor stations are only available on ships of ${maxShipTonnage} tons or less`,
      );
    }
  }

  return errors;
};

// Traveller rules-accurate drone calculations
export function calculateMiningDroneSpecs(quantity: number = 5) {
  // Each set of 5 drones = 10 tons, MCr1
  const sets = Math.ceil(quantity / 5);
  return {
    cost: sets * 1000000, // MCr1 per 10 tons
    tonnage: sets * 10,
    power: 0,
    techLevel: 12,
    note: `${sets * 5} mining drones, ${sets * 10} tons, MCr${sets}`,
  };
}

export function calculateProbeDroneSpecs(quantity: number = 5) {
  // Each set of 5 drones = 1 ton, MCr0.5
  const sets = Math.ceil(quantity / 5);
  return {
    cost: sets * 500000, // MCr0.5 per 1 ton
    tonnage: sets * 1,
    power: 0,
    techLevel: 9,
    note: `${sets * 5} probe drones, ${sets * 1} tons, MCr${sets * 0.5}`,
  };
}

export function calculateAdvancedProbeDroneSpecs(quantity: number = 5) {
  // Each set of 5 drones = 1 ton, MCr0.8
  const sets = Math.ceil(quantity / 5);
  return {
    cost: sets * 800000, // MCr0.8 per 1 ton
    tonnage: sets * 1,
    power: 0,
    techLevel: 12,
    note: `${sets * 5} advanced probe drones, ${sets * 1} tons, MCr${sets * 0.8}`,
  };
}

export function calculateRepairDroneSpecs(shipTonnage: number) {
  // 1% of ship tonnage (min 1 ton), MCr0.2 per ton
  const tonnage = Math.max(1, Math.ceil(shipTonnage * 0.01));
  return {
    cost: tonnage * 200000, // MCr0.2 per ton
    tonnage,
    power: 0,
    techLevel: 10,
    note: `${tonnage} tons repair drones, MCr${tonnage * 0.2}`,
  };
}

// Rules-accurate validation for each drone type
export function validateMiningDrone(quantity: number) {
  const errors: string[] = [];
  if (quantity < 5) errors.push("Minimum 5 mining drones per set");
  return errors;
}
export function validateProbeDrone(quantity: number) {
  const errors: string[] = [];
  if (quantity < 5) errors.push("Minimum 5 probe drones per set");
  return errors;
}
export function validateAdvancedProbeDrone(quantity: number) {
  const errors: string[] = [];
  if (quantity < 5) errors.push("Minimum 5 advanced probe drones per set");
  return errors;
}
export function validateRepairDrone(shipTonnage: number) {
  const errors: string[] = [];
  if (shipTonnage < 100)
    errors.push("Ship must be at least 100 tons for 1% repair drone coverage");
  return errors;
}

// Traveller rules-accurate sensor calculations
export function calculateDeepPenetrationScannersSpecs(tons: number) {
  // MCr1/ton, 1 power/ton
  return {
    cost: tons * 1000000,
    tonnage: tons,
    power: tons * 1,
    techLevel: 13,
    note: `${tons} tons, scans ${tons * 20} tons/hour, MCr${tons}`,
  };
}
export function calculateExtensionNetSpecs(shipTonnage: number) {
  // 1% of ship tonnage (min 1 ton), MCr1/ton
  const tons = Math.max(1, Math.ceil(shipTonnage * 0.01));
  return {
    cost: tons * 1000000,
    tonnage: tons,
    power: 0,
    techLevel: 10,
    note: `${tons} tons, MCr${tons}`,
  };
}
export function calculateDistributedArraySpecs(attachedSuite: {
  tonnage: number;
  cost: number;
  power: number;
}) {
  // Triples tonnage, cost, power
  return {
    cost: attachedSuite.cost * 3,
    tonnage: attachedSuite.tonnage * 3,
    power: attachedSuite.power * 3,
    techLevel: 11,
    note: `Tripled: ${attachedSuite.tonnage * 3} tons, MCr${attachedSuite.cost * 3}`,
  };
}
export function calculateExtendedArraySpecs(attachedSuite: {
  tonnage: number;
  cost: number;
  power: number;
}) {
  // Ensure cost is in Cr
  const baseCost =
    attachedSuite.cost < 1000
      ? attachedSuite.cost * 1_000_000
      : attachedSuite.cost;
  return {
    cost: baseCost * 3,
    tonnage: attachedSuite.tonnage * 3,
    power: attachedSuite.power * 3,
    techLevel: 11,
    note: `Tripled: ${attachedSuite.tonnage * 3} tons, MCr${(baseCost * 3) / 1_000_000}`,
  };
}
export function calculateRapidDeploymentExtendedArraySpecs(attachedSuite: {
  tonnage: number;
  cost: number;
  power: number;
}) {
  // Ensure cost is in Cr
  const baseCost =
    attachedSuite.cost < 1000
      ? attachedSuite.cost * 1_000_000
      : attachedSuite.cost;
  return {
    cost: baseCost * 5,
    tonnage: attachedSuite.tonnage * 3,
    power: attachedSuite.power * 3,
    techLevel: 11,
    note: `Tripled: ${attachedSuite.tonnage * 3} tons, MCr${(baseCost * 5) / 1_000_000}`,
  };
}
// Validation functions
export function validateDeepPenetrationScanners(tons: number) {
  const errors: string[] = [];
  if (tons < 1) errors.push("Minimum 1 ton for Deep Penetration Scanners");
  return errors;
}
export function validateExtensionNet(shipTonnage: number) {
  const errors: string[] = [];
  if (shipTonnage < 100)
    errors.push("Ship must be at least 100 tons for 1% extension net");
  return errors;
}
export function validateDistributedArray(
  attachedSuite: { tonnage: number; cost: number; power: number },
  shipTonnage: number,
  isImprovedOrAdvanced: boolean,
) {
  const errors: string[] = [];
  if (!isImprovedOrAdvanced)
    errors.push("Distributed Array requires Improved or Advanced suite");
  if (shipTonnage <= 5000)
    errors.push("Distributed Array requires ship > 5000 tons");
  return errors;
}
export function validateExtendedArray(attachedSuite: {
  tonnage: number;
  cost: number;
  power: number;
}) {
  return [];
}
export function validateRapidDeploymentExtendedArray(attachedSuite: {
  tonnage: number;
  cost: number;
  power: number;
}) {
  return [];
}
